import { CancelIcon, CustomIcon, MoveIcon, RestoreIcon } from "./icons.tsx";

/**
 * Ряд значков у занятия серии: перенести, сделать особым, отменить, вернуть.
 *
 * Отдельным файлом от `EntryTools.tsx` с 30.08.2026, вместе с двумя формами:
 * файл переваливал за четыреста строк, а сама функция за двести семьдесят при
 * потолке в сто пятьдесят (раздел 9 CLAUDE.md). Границы проведены по режимам,
 * ровно как их уже провели `OverrideForm.tsx` и `SingleEntryTools.tsx`, и
 * поведение при переезде не менялось: состояние осталось у `EntryTools`, сюда
 * приезжают значения, отсюда уезжают события.
 *
 * ⚠️ **Инструменты это значки, а главное действие это слово.** Три текстовые
 * кнопки («Перенести», «Отменить», «Сделать особым») занимали в карточке больше
 * места, чем сведения о самом занятии, и человек читал их каждый раз заново,
 * хотя открывал карточку ради «Войти». Значок тут законен ровно потому, что
 * действие второстепенное и повторяющееся; у «Войти» и «Подготовить» смысл
 * разный, одной фигурой их не различить, и они остались словами. Подпись у
 * каждого значка есть всегда: `aria-label` для читалки и `title` для наведения.
 */
export function EntryToolButtons({
  personal,
  customized,
  parked,
  canEnd,
  busy,
  onPick,
  onRestore,
}: {
  /**
   * Занятие пары, а не группы.
   *
   * ⚠️ **Четвёртого инструмента, «Сделать особым», у группы НЕТ, и это не
   * забывчивость.** Правка одного занятия отвечает пучку
   * `group_schedule_managed` (`routes/schedule/overrides.ts`), потому что
   * правка, доехавшая до половины пучка, это расписание, которое врёт обеим
   * сторонам. Нужда бытовая («в этот четверг у группы другая тема»), поэтому
   * дорисовать значок в отказ соблазнительно, и делать этого нельзя: кнопка,
   * ведущая в 409, хуже отсутствующей. Ровно это чинили 29.08.2026, когда у
   * сводной строки временно убрали все три значка. Значок появится в тот день,
   * когда появится ручка.
   */
  personal: boolean;
  /** Правка этого занятия уже применена: видно на самом значке, не только в подписи. */
  customized: boolean;
  /** Занятие отменено или уехало: остаётся ровно одно действие, вернуть его. */
  parked: boolean;
  /**
   * В форме отмены будет строка «Убрать это и все следующие» (завершение серии
   * с даты этого занятия). Подпись значка называет оба действия, иначе второе
   * нашлось бы только случайно, а значок один и тот же.
   */
  canEnd: boolean;
  busy: boolean;
  onPick: (mode: "cancel" | "move" | "custom") => void;
  onRestore: () => void;
}) {
  if (parked) {
    return (
      // Возврат занятия ничего не рушит, а чинит: обычный значок, не красный
      <button
        type="button"
        className="btn btn--ico btn--quiet"
        disabled={busy}
        title="Вернуть занятие в расписание"
        aria-label="Вернуть занятие"
        onClick={onRestore}
      >
        <RestoreIcon />
      </button>
    );
  }

  const cancelLabel = canEnd ? "Отменить занятие или убрать все следующие" : "Отменить занятие";

  return (
    <>
      <button
        type="button"
        className="btn btn--ico btn--quiet"
        disabled={busy}
        title="Перенести на другой день или час"
        aria-label="Перенести занятие"
        onClick={() => onPick("move")}
      >
        <MoveIcon />
      </button>
      {/*
        Правка ОДНОГО занятия, а не всей серии. Рядом с переносом потому, что
        человек думает про это одинаково: «в этот четверг у нас не как обычно».
        У группы этого значка нет, разбор в подписи `personal`

        ⚠️ Уже применённая правка отмечена на самом значке (`data-on`), а не
        только словами в подписи: иначе «особое» занятие ничем не отличалось бы
        от обычного, пока не наведёшь
      */}
      {personal && (
        <button
          type="button"
          className="btn btn--ico btn--quiet"
          data-on={customized}
          disabled={busy}
          title={
            customized
              ? "Изменить правку этого занятия"
              : "Сделать это занятие особым: своё название, длительность, заготовка"
          }
          aria-label={customized ? "Изменить это занятие" : "Сделать занятие особым"}
          onClick={() => onPick("custom")}
        >
          <CustomIcon />
        </button>
      )}
      <button
        type="button"
        className="btn btn--ico btn--danger"
        disabled={busy}
        title={cancelLabel}
        aria-label={cancelLabel}
        onClick={() => onPick("cancel")}
      >
        <CancelIcon />
      </button>
    </>
  );
}
